'use client';

import { useTranslation } from '@/i18n/LocaleProvider';
import { T } from '@/i18n/LocaleProvider';
import { Fragment, useRef, useEffect, useLayoutEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Button, Logo } from '@/components';
import styles from './Navbar.module.scss';
import {
  NAV_LINKS_ALL,
  FOOTER_LINKS_ALL,
  filterByPhase,
} from '@/constants/phases';
import { demoCTA } from '@/libs/cta';
import NavDropdown from './NavDropdown';
import { isStandalone } from './standalone';

const NAV_LINKS = filterByPhase(NAV_LINKS_ALL);
// Estructura del topbar: grupos desplegables ("Producto", "Recursos") y links sueltos. Los links salen
// de las fuentes únicas de phases.js (con su filtro de fase): "Producto" toma sus 5 páginas de NAV_LINKS_ALL,
// "Planes" agrupa los planes para empresas y el programa para agencias, y "Recursos" reutiliza el grupo del
// footer. Un ítem con `links` es un grupo; con `href`, un link.
const byHref = Object.fromEntries(NAV_LINKS.map((l) => [l.href, l]));
const PLANES_LABELS = {
  '/planes': 'Para empresas',
  '/agencias': 'Para agencias',
};
const NAV_ITEMS = [
  {
    label: 'Producto',
    links: ['/plataforma', '/metodologia', '/tecnologia', '/seo', '/mcp', '/sostenibilidad']
      .map((h) => byHref[h])
      .filter(Boolean),
  },
  {
    label: 'Planes',
    links: Object.keys(PLANES_LABELS)
      .map((h) => byHref[h] && { ...byHref[h], label: PLANES_LABELS[h] })
      .filter(Boolean),
  },
  { label: 'Recursos', links: filterByPhase(FOOTER_LINKS_ALL.Recursos) },
  byHref['/nosotros'],
].filter((i) => i && (!i.links || i.links.length));
const isCurrent = (pathname, href) =>
  pathname === href || (href !== '/' && pathname.startsWith(`${href}/`));

// Approximate navbar height used to probe which section is behind it
const NAV_H = 60;

const THEMES = {
  dark: {
    backgroundColor: 'rgba(10,15,14,0.82)',
    borderBottomColor: 'rgba(255,255,255,0.06)',
  },
  light: {
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderBottomColor: 'rgba(31,36,34,0.08)',
  },
};

// Equivalente CSS del antiguo gsap.to(..., { duration: 0.3, ease: 'power2.out' }): se evita cargar
// gsap (≈50 KB) en TODAS las páginas solo para animar dos colores del navbar.
const THEME_TRANSITION =
  'background-color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1), border-bottom-color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1)';

function paintTheme(nav, theme, animate) {
  nav.style.transition = animate ? THEME_TRANSITION : 'none';
  Object.assign(nav.style, THEMES[theme]);
}

const normPath = (p) => (p === '/' ? '/' : p.replace(/\/$/, ''));

// El menú no va en las páginas de salida (/enlaces): ver standalone.js.
export default function Navbar() {
  const pathname = usePathname();
  return isStandalone(pathname) ? null : <SiteNavbar />;
}

function SiteNavbar() {
  const t = useTranslation();
  const navRef = useRef(null);
  const rawPath = usePathname();
  const pathname = normPath(rawPath ?? '/');
  const [dark, setDark] = useState(pathname === '/');
  const [menuOpen, setMenuOpen] = useState(false);

  // Apply correct initial background before first paint (no flash)
  useLayoutEffect(() => {
    if (!navRef.current) return;
    const theme = pathname === '/' ? 'dark' : 'light';
    paintTheme(navRef.current, theme, false);
    setDark(theme === 'dark');
  }, [pathname]);

  // Scrollspy vía IntersectionObserver — evita leer getBoundingClientRect en
  // cada scroll (forced reflow). Se observa una banda de 1px a la altura de
  // la navbar; la sección que la cruza dispara el cambio de tema.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const sections = document.querySelectorAll('[data-nav-theme]');
    if (!sections.length) return;

    const applyTheme = (theme, animate = true) => {
      setDark(theme === 'dark');
      paintTheme(nav, theme, animate);
    };

    const bandBottom = Math.max(window.innerHeight - NAV_H - 1, 0);
    let isInitialBatch = true;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            applyTheme(
              entry.target.dataset.navTheme ?? 'light',
              !isInitialBatch
            );
          }
        });
        isInitialBatch = false;
      },
      { rootMargin: `-${NAV_H}px 0px -${bandBottom}px 0px`, threshold: 0 }
    );

    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [pathname]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <nav ref={navRef} className={`${styles.nav} ${dark ? styles.dark : ''}`}>
      <div className={styles.inner}>
        <Link href="/" className={styles.logo} aria-label="Bison Agents">
          <Logo dark={dark} />
        </Link>

        <ul className={styles.links}>
          {NAV_ITEMS.map((item) => {
            if (item.links) {
              return (
                <li key={item.label}>
                  <NavDropdown
                    label={item.label}
                    links={item.links}
                    pathname={pathname}
                  />
                </li>
              );
            }
            const { label, href } = item;
            const isActive = pathname === href;
            return (
              <li key={href}>
                <Link href={href} className={isActive ? styles.active : ''}>
                  <T>{label}</T>
                  {isActive && (
                    <span
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: dark
                          ? 'var(--teal-glow, #1abf9f)'
                          : '#009f8b',
                      }}
                    />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className={styles.cta}>
          <Button
            href={demoCTA.url}
            external
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            data-gtm-event="demo_request"
            data-gtm-source="navbar"
          >
            <T>Agendar demo</T>
          </Button>
        </div>

        <button
          className={`${styles.hamburger} ${menuOpen ? styles.open : ''}`}
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={t(menuOpen ? 'Cerrar menú' : 'Abrir menú')}
          aria-expanded={menuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {menuOpen && (
        <div className={styles.mobileMenu}>
          <ul>
            {NAV_ITEMS.map((item) =>
              item.links ? (
                <Fragment key={item.label}>
                  <li className={styles.groupLabel}>
                    <T>{item.label}</T>
                  </li>
                  {item.links.map((l) => (
                    <li key={l.href} className={styles.groupItem}>
                      <Link
                        href={l.href}
                        className={
                          isCurrent(pathname, l.href) ? styles.active : ''
                        }
                      >
                        <T>{l.label}</T>
                      </Link>
                    </li>
                  ))}
                </Fragment>
              ) : (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className={pathname === item.href ? styles.active : ''}
                  >
                    <T>{item.label}</T>
                  </Link>
                </li>
              )
            )}
          </ul>
          <div className={styles.mobileCta}>
            <Button
              href={demoCTA.url}
              external
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              fullWidth
              data-gtm-event="demo_request"
              data-gtm-source="navbar_mobile"
            >
              <T>Agendar demo</T>
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
